'use client';

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface USP {
  id: string;
  title: string;
  description: string;
  icon: string;
}

export default function USPSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const usps: USP[] = [
    {
      id: 'usp_practical',
      title: 'Practical Learning',
      description: 'Learn by doing with hands-on projects, real-world scenarios, and live coding sessions that prepare you for actual work environments',
      icon: 'WrenchScrewdriverIcon',
    },
    {
      id: 'usp_assignment',
      title: 'Assignment-Based Learning',
      description: 'Master concepts through carefully designed assignments that reinforce learning and build your portfolio with practical work',
      icon: 'DocumentCheckIcon',
    },
    {
      id: 'usp_attention',
      title: 'Individual Attention',
      description: 'Small batch sizes ensure personalized guidance, one-on-one mentorship, and tailored learning paths for every student',
      icon: 'UserIcon',
    },
    {
      id: 'usp_infrastructure',
      title: 'Best-in-Class Infrastructure',
      description: 'State-of-the-art computer labs, high-speed internet, modern equipment, and comfortable learning spaces for optimal education',
      icon: 'BuildingOfficeIcon',
    },
    {
      id: 'usp_experts',
      title: 'Learn from Industry Experts',
      description: 'Gain insights from professionals with years of real-world experience who bring industry best practices to the classroom',
      icon: 'AcademicCapIcon',
    },
    {
      id: 'usp_handson',
      title: 'Live Hands-On Experience',
      description: 'Work on live projects, participate in real client work, and gain practical experience that employers value',
      icon: 'CommandLineIcon',
    },
    {
      id: 'usp_placement',
      title: 'Career Support',
      description: 'Resume building, interview preparation, and job placement assistance to help you launch your career successfully',
      icon: 'BriefcaseIcon',
    },
    {
      id: 'usp_flexible',
      title: 'Flexible Learning Options',
      description: 'Weekend batches, evening classes, and customized schedules to fit your lifestyle and commitments',
      icon: 'ClockIcon',
    },
  ];

  return (
    <section ref={sectionRef} className="py-20 md:py-32 px-6 bg-background">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className={`text-center mb-16 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
          <span className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-6">
            <Icon name="SparklesIcon" size={16} className="text-primary" />
            <span className="text-xs font-semibold uppercase tracking-wider text-primary">
              Why Choose Us
            </span>
          </span>
          <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tighter leading-none text-foreground mb-4">
            What Makes Us<br />
            <span className="text-primary">Stand Out</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Our unique approach to education combines practical training with industry expertise to deliver exceptional learning outcomes
          </p>
        </div>

        {/* USP Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {usps.map((usp, index) => (
            <div
              key={usp.id}
              className={`group relative bg-card border border-border rounded-2xl p-8 hover:border-primary/50 hover:shadow-xl transition-all duration-300 ${
                isVisible ? 'animate-fadeIn' : 'opacity-0'
              }`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Icon */}
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-primary/10 mb-6 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                <Icon name={usp.icon as any} size={32} className="text-primary group-hover:text-primary-foreground transition-colors" />
              </div>

              {/* Content */}
              <h3 className="font-display text-2xl uppercase tracking-tight text-foreground mb-3">
                {usp.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {usp.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}